import { Button, TextLink } from '@edsn/pie-components';
import phoneIcon from '@iconify/icons-material-symbols/call';
import mailIcon from '@iconify/icons-material-symbols/mail-outline-rounded';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { AuthorizationTag } from '#pie/components/authorization-tag/AuthorizationTag';
import { Card } from '#pie/components/card/Card';
import { IconButton } from '#pie/components/icon-button/IconButton';
import { InfoDialog } from '#pie/components/info-dialog/InfoDialog';
import { InputField } from '#pie/components/input-field/InputField';
import { InputText } from '#pie/components/input-text/InputText';
import { Page, PageHeader } from '#pie/components/page/Page';
import { Skeleton } from '#pie/components/skeleton/Skeleton';
import { SkeletonLines } from '#pie/components/skeleton-lines/SkeletonLines';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  AuthorizationRoles,
  type CreateUpdateContactDetailsRequest,
  getGetContactDetailsIdQueryKey,
  type UpdateContactInformationRequest,
  useGetContactDetails,
  useGetContactDetailsId,
  useGetCprContacts,
  usePutContactDetailsMyInformation,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { FormPhone } = typedFormFields<CreateUpdateContactDetailsRequest>();

export const Account = () => {
  const { t: tcom } = useTranslation();
  const { t: thl } = useTranslation('hyperlinks');
  const { t: tom } = useTranslation('organisationManagement');
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { currentAccount } = useCurrentOrganisation();
  const userId = currentAccount?.userId;
  const hasCprRole = useUserHasRole({ role: AuthorizationRoles.CprRead });
  const organisationIds = (currentAccount?.subOrganisations ?? []).map(
    (organisation) => organisation.id,
  );

  const { data: user, isLoading } = useGetContactDetailsId(userId!, {
    query: { enabled: !!userId, refetchOnWindowFocus: false },
  });

  const { data: superUserContacts, isLoading: isSuperUserLoading } =
    useGetContactDetails({
      filter: { roles: ['SuperUser'] },
      pagination: { pageIndex: 0, pageSize: 100 },
    });

  const { data: cprContacts, isLoading: isCprContactsLoading } =
    useGetCprContacts(
      {
        filter: {
          organisationIds,
        },
        pagination: { pageIndex: 0, pageSize: 100 },
        search: user?.email ?? undefined,
      },
      {
        query: {
          enabled: hasCprRole && !!user?.email && organisationIds.length > 0,
          refetchOnWindowFocus: false,
        },
      },
    );

  const contactGroups = [
    ...new Map(
      cprContacts?.items?.map((item) => [
        item.contactGroup.id,
        item.contactGroup,
      ]),
    ).values(),
  ];

  const formMethods = useForm<UpdateContactInformationRequest>();

  useEffect(() => {
    formMethods.reset({
      ...user,
      phone: user?.phone || '',
    });
  }, [user]);

  const { mutate, isPending: isMutating } = usePutContactDetailsMyInformation({
    mutation: {
      onError: (error) => {
        addToast({
          message: tcom('account.toast.error.message'),
          title: tcom('account.toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: tcom('account.toast.succes.message'),
          title: tcom('common.string.user_adjusted'),
          type: 'success',
        });
        userId &&
          queryClient.invalidateQueries({
            queryKey: getGetContactDetailsIdQueryKey(userId),
          });
      },
    },
  });

  const onSubmit: SubmitHandler<UpdateContactInformationRequest> = (data) => {
    mutate({ data: { phone: data.phone } });
  };

  return (
    <Page>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(onSubmit)}
          aria-label={tcom('account.title')}
        >
          <PageHeader title={tcom('account.title')} backHref={'../'}>
            <Button
              type="submit"
              isLoading={isLoading || isMutating}
              action="secondary"
              emphasis="bold"
              disabled={!formMethods.formState.isDirty}
            >
              {tom('header.button.submit_changes')}
            </Button>
          </PageHeader>
          <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
            <Card
              size="md"
              heading={
                <Stack
                  direction="row"
                  gap="xs"
                  className="text-content-heading"
                >
                  <Text as="h6" variant="h6">
                    {tcom('common.personal_info')}
                  </Text>
                  <InfoDialog title={tcom('account.info.title')}>
                    {tcom('account.info.message')}
                  </InfoDialog>
                </Stack>
              }
            >
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <InputField name="firstName" label={tcom('common.first_name')}>
                  <InputText
                    name="firstName"
                    fullWidth
                    disabled
                    value={user?.firstName ?? ''}
                    isLoading={isLoading}
                  />
                </InputField>
                <InputField
                  name="lastName"
                  label={tcom('common.last_name_prefix')}
                >
                  <InputText
                    name="lastName"
                    fullWidth
                    disabled
                    value={user?.lastName ?? ''}
                    isLoading={isLoading}
                  />
                </InputField>
                <InputField name="email" label={tcom('common.email')}>
                  <InputText
                    name="email"
                    type="email"
                    fullWidth
                    disabled
                    value={user?.email ?? ''}
                    isLoading={isLoading}
                  />
                </InputField>
                <FormPhone name="phone" />
                <InputField name="password" label={tcom('common.password')}>
                  <Stack gap="xs">
                    <InputText
                      name="password"
                      type="password"
                      fullWidth
                      disabled
                      value="*********"
                    />
                    <TextLink
                      target="_blank noreferror noopener"
                      href={thl('dvt_prod')}
                      className="mt-2"
                    >
                      {tcom('account.form.password.hint')}
                    </TextLink>
                  </Stack>
                </InputField>
              </div>
            </Card>
            <Card
              size="md"
              heading={
                <Text as="h6" variant="h6">
                  {tcom('common.roles')}
                </Text>
              }
            >
              <div className="flex flex-wrap gap-2">
                {isLoading ? (
                  <Stack direction="row" gap="sm">
                    {Array(3)
                      .fill(0)
                      .map((_, index) => (
                        <Skeleton key={index} className="w-16" />
                      ))}
                  </Stack>
                ) : user?.roles && user.roles.length > 0 ? (
                  user?.roles
                    .filter(
                      (role) => role !== AuthorizationRoles.SimpleDataRequest,
                    )
                    .map((m) => <AuthorizationTag key={m} role={m} />)
                ) : (
                  <Text>{tcom('account.empty.roles')}</Text>
                )}
              </div>
            </Card>
            <Card
              size="md"
              heading={
                <Text as="h6" variant="h6">
                  {tcom('common.admin', {
                    count: superUserContacts?.items.length || 2,
                  })}
                </Text>
              }
            >
              <Stack>
                {isSuperUserLoading ? (
                  <SkeletonLines lines={3} />
                ) : (
                  <Stack gap="xs">
                    {superUserContacts?.items.map((contact) => (
                      <div className="flex flex-row gap-1" key={contact.id}>
                        <Card className="bg- grow bg-neutral-100 px-5 py-3">
                          {contact.fullName}
                        </Card>
                        <IconButton
                          icon={mailIcon}
                          as={'a'}
                          href={`mailto:${contact.email}`}
                          className="rounded-md px-3 py-3"
                        />
                        <IconButton
                          icon={phoneIcon}
                          as={'a'}
                          href={`tel:${contact.phone}`}
                          className="rounded-md px-3 py-3"
                        />
                      </div>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>
            <Card
              size="md"
              heading={
                <Stack
                  direction="row"
                  gap="xs"
                  className="text-content-heading"
                >
                  <Text as="h6" variant="h6">
                    {tcom('account.team', { count: user?.teams.length || 2 })}
                  </Text>
                  <InfoDialog title={tom('teams.info.title.about_teams')}>
                    {tom('teams.info_message')}
                  </InfoDialog>
                </Stack>
              }
            >
              <Stack gap="md">
                {isLoading ? (
                  <SkeletonLines lines={3} />
                ) : user?.teams && user.teams.length > 0 ? (
                  <Stack gap="sm">
                    {user?.teams.map((item) => (
                      <Card className="px-5 py-3" key={item.id}>
                        {item.label}
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Text>{tcom('account.empty.teams')}</Text>
                )}
              </Stack>
            </Card>
            {hasCprRole && (
              <Card
                size="md"
                heading={
                  <Stack
                    direction="row"
                    gap="xs"
                    className="text-content-heading"
                  >
                    <Text as="h6" variant="h6">
                      {tcom('account.contact_group', {
                        count: contactGroups.length || 2,
                      })}
                    </Text>
                  </Stack>
                }
              >
                <Stack gap="md">
                  {isCprContactsLoading ? (
                    <SkeletonLines lines={3} />
                  ) : contactGroups.length > 0 ? (
                    <Stack gap="sm">
                      {contactGroups.map((group) => (
                        <Card className="px-5 py-3" key={group.id}>
                          {group.label}
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Text>{tcom('account.empty.contact_groups')}</Text>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </form>
      </FormProvider>
    </Page>
  );
};
